import React, { FC, useEffect, useMemo, useState } from 'react'
import Modal from '../common/Modal'
import { format, nowDate } from '../../../utils/date'
import { ResponsePostTaskLabelGroupImport } from '../../../states/types/task'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import COLOR_LIST from '../../constants/taskColor'
import { useDispatch } from 'react-redux'
import { createTaskLabelGroupImport, downloadCsvData } from '../../../states/containers/task/taskSlice'
import LoadingSquare from '../common/LoadingSquare'

interface Props {
  isDisplay: boolean
  dataList: ResponsePostTaskLabelGroupImport | undefined
  closeCallback: VoidFunction
  addCallback: () => void
  isCost: boolean
}

type ActionType = "create" | "update" | "destroy" | "no_change"

const DISPLAY_LIMIT = 100

const ImportProjectModal: FC<Props> = (props) => {
  const {
    isDisplay,
    dataList,
    closeCallback,
    addCallback,
    isCost,
  } = props

  const dispatch = useDispatch()

  const [fileData, setFileData] = useState<Blob>()
  const [step, setStep] = useState<number>(1)
  const [csvErrorList, setCsvErrorList] = useState<{column_name: string, row_number: number, message: string}[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const openListInit = ["create", "update", "destroy"]
  const [openList, setOpenList] = useState<string[]>(openListInit)

  const [isDownloading, setIsDownloading] = useState<boolean>(false)

  const [displayNum, setDisplayNum] = useState<{create: number, update: number, destroy: number, no_change: number}>({
    create: 1,
    update: 1,
    destroy: 1,
    no_change: 1,
  })

  const [totalCount, setTotalCount] = useState<number>(0)

  const resultDisplay = useMemo(() => totalCount < 1000, [totalCount])

  const downloadCsv = (code: string) => {
    setIsDownloading(true)
    dispatch(
      downloadCsvData(
        code,
        () => {
          setIsDownloading(false)
        },
      ),
    )
  }

  const uploadCsv = (uploadFile: FileList | null) => {
    if (!uploadFile) return
    setFileData(uploadFile[0])
    setIsLoading(true)
    setCsvErrorList([])
    const formData = new FormData()
    formData.append('csv_file', uploadFile[0])
    formData.append('json_data', '{ "register": false }')
    dispatch(createTaskLabelGroupImport(
      formData,
      (result) => {
        if (!result.errors.length) {
          setStep(3)
          setTotalCount(result.count.create + result.count.destroy + result.count.no_change + result.count.update)
        }
        setIsLoading(false)
      },
      () => {
        setIsLoading(false)
      },
    ))
  }

  const prev = () => {
    setStep(step - 1)
    setFileData(undefined)
    setCsvErrorList([])
    setOpenList(openListInit)
    setTotalCount(0)
    setDisplayNum({
      create: 1,
      update: 1,
      destroy: 1,
      no_change: 1,
    })
  }

  const submit = () => {
    if (dataList?.errors.length) return
    if (!fileData) return
    setIsLoading(true)
    const formData = new FormData()
    formData.append('csv_file', fileData)
    formData.append('json_data', '{ "register": true }')
    dispatch(createTaskLabelGroupImport(
      formData,
      () => {
        setStep(4)
        setIsLoading(false)
        addCallback()
      },
      () => {
        setIsLoading(false)
      },
    ))
  }

  const buildButtonArea = () => {
    let prevButton = (
      <button
        type="button"
        className="button cancel"
        onClick={prev}
      >
        戻る
      </button>
    )
    let nextButton = (
      <button
        type="button"
        className="button"
        onClick={() => setStep(step + 1)}
        disabled={step === 2 && (!fileData || !!dataList?.errors.length)}
      >
        次へ
      </button>
    )
    if (step === 1) {
      prevButton = <></>
    }
    if (step === 2) {
      nextButton = <></>
      if (isLoading) {
        prevButton = <></>
      }
    }
    if (step === 3) {
      nextButton = (
        <button
          type="button"
          className="button"
          onClick={submit}
        >
          一括登録する
        </button>
      )
    }
    if (step === 4) {
      prevButton = <></>
      nextButton = (
        <button
          type="button"
          className="button cancel"
          onClick={() => closeCallback()}
        >
          閉じる
        </button>
      )
    }
    if (isLoading && step !== 2) {
      prevButton = (
        <button
          type="button"
          className="button cancel"
          disabled
        >
          <FontAwesomeIcon icon={['fas', 'spinner']} spin />
        </button>
      )
      nextButton = (
        <button
          type="button"
          className="button"
          disabled
        >
          <FontAwesomeIcon icon={['fas', 'spinner']} spin />
        </button>
      )
    }
    return (
      <div className="button_area">
        {prevButton}
        {nextButton}
      </div>
    )
  }

  const buildProjectAction = (action: string) => {
    let text = ""
    let bgColor = "#666666"
    if (action === "create") {
      text = "追加"
      bgColor = "#0a8a48"
    } else if (action === "update") {
      text = "変更あり"
      bgColor = "#1346ef"
    } else if (action === "destroy") {
      text = "削除"
      bgColor = "#d10000"
    } else if (action === "no_change") {
      text = "変更なし"
      bgColor = "#4d5a7b"
    }

    return (
      <div
        style={{
          backgroundColor: bgColor,
          color: "#FFFFFF",
          borderRadius: "4px",
          padding: "2px 4px",
          minWidth: "40px",
        }}
      >
        {text}
      </div>
    )
  }

  const changeClickAction = (action: string) => {
    if (openList.includes(action)) {
      setOpenList((self) => self.filter((v) => v !== action))
    } else {
      setOpenList((self) => [...self, action])
    }
  }

  const buildProjectActionBar = (action: ActionType) => {
    let text = ""
    let bgColor = "#666666"
    if (action === "create") {
      text = "追加"
      bgColor = "#0a8a48"
    } else if (action === "update") {
      text = "変更あり"
      bgColor = "#1346ef"
    } else if (action === "destroy") {
      text = "削除"
      bgColor = "#d10000"
    } else if (action === "no_change") {
      text = "変更なし"
      bgColor = "#4d5a7b"
    }

    return (
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: bgColor,
          color: "#FFFFFF",
          padding: "4px 8px",
          cursor: resultDisplay ? "pointer" : "default",
          userSelect: "none",
        }}
        onClick={() => resultDisplay && changeClickAction(action)}
        role="none"
      >
        <div
          style={{
            display: "flex",
            gap: "8px",
            alignItems: "center",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: "8px",
              alignItems: "baseline",
            }}
          >
            <div>
              {text}
            </div>
            <div
              style={{
                display: "flex",
                gap: "2px",
                alignItems: "baseline",
              }}
            >
              <div
                style={{
                  fontSize: "20px",
                }}
              >
                {dataList?.count[action] || 0}
              </div>
              件
            </div>
          </div>
          {
            resultDisplay && (
              <div
                style={{
                  border: "solid 1px #999999",
                  borderRadius: "4px",
                  padding: "2px 4px",
                  fontSize: "10px",
                  color: "#333333",
                  backgroundColor: "#FFFFFF",
                }}
              >
                {openList.includes(action) ? "閉じる" : "展開する"}
              </div>
            )
          }
        </div>
        {
          resultDisplay && (
            <FontAwesomeIcon
              icon={['fas', openList.includes(action) ? 'angle-down' : 'angle-right']}
              style={{
                fontSize: "16px",
              }}
            />
          )
        }
      </div>
    )
  }

  const buildProjectList = (action: ActionType) => (
    <>
      {
        dataList?.label_groups.filter((v) => v.action === action).slice(0, (displayNum[action] * DISPLAY_LIMIT)).map((data) => (
          <div
            key={`data_list_${data.name}`}
            className="result_row"
          >
            <div className="name_area">
              {buildProjectAction(data.action)}
              {
                data.color ? (
                  <div
                    className="icon_color"
                    style={{
                      backgroundColor: data.color || "#CCCCCC",
                    }}
                  />
                ) : (
                  <div
                    style={{
                      border: "solid 1px #999999",
                      borderRadius: "4px",
                      fontSize: "10px",
                      padding: "2px",
                      color: "#999999",
                      backgroundColor: "#FFFFFF",
                    }}
                  >
                    色指定なし
                  </div>
                )
              }
              {data.name}
              {data.code && `(${data.code})`}
            </div>
            <div className="schedule_area">
              {`${format(data.scheduled_start_date)} ~ ${data.scheduled_end_date ? format(data.scheduled_end_date) : ""}`}
            </div>
            <div className="detail_area">
              <div className="block_area">
                <div className="title_area">
                  業務
                </div>
                <div className="chip_area">
                  {
                    data.labels.map((label) => (
                      <div className="chip">
                        {label.name}
                        {label.code && `(${label.code})`}
                        {
                          label.registration_type === "new" && (
                            <div className="new">
                              new
                            </div>
                          )
                        }
                      </div>
                    ))
                  }
                </div>
              </div>
              <div className="block_area">
                <div className="title_area">
                  プロジェクト表示チーム・メンバー
                </div>
                <div className="chip_area">
                  {
                    data.permission.viewable.teams.map((team) => (
                      <div className="chip">
                        {team.name}
                      </div>
                    ))
                  }
                  {
                    data.permission.viewable.members.map((member) => (
                      <div className="chip">
                        {member.name}
                      </div>
                    ))
                  }
                </div>
              </div>
              <div className="block_area">
                <div className="title_area">
                  プロジェクト参加チーム・メンバー
                </div>
                <div className="chip_area">
                  {
                    data.permission.usable.teams.map((team) => (
                      <div className="chip">
                        {team.name}
                      </div>
                    ))
                  }
                  {
                    data.permission.usable.members.map((member) => (
                      <div className="chip">
                        {member.name}
                      </div>
                    ))
                  }
                </div>
              </div>
              {
                !!data.auto_scheduled_times.length && (
                  <div className="block_area">
                    <div className="title_area">
                      予定時間
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexFlow: "column",
                        gap: "4px",
                      }}
                    >
                      {
                        data.auto_scheduled_times.map((v) => (
                          <div
                            style={{
                              borderBottom: "dashed 1px #CCCCCC",
                              padding: "2px 8px",
                              display: "flex",
                              gap: "8px",
                            }}
                          >
                            <div>
                              {format(v.start_date, "YYYY年M月")}
                            </div>
                            <div>
                              ~
                            </div>
                            <div>
                              {v.end_date ? format(v.end_date, "YYYY年M月") : "指定なし"}
                            </div>
                            <div>
                              {`${v.time}時間`}
                            </div>
                          </div>
                        ))
                      }
                    </div>
                  </div>
                )
              }
              {
                !!data.auto_budgets.length && (
                  <div className="block_area">
                    <div className="title_area">
                      予算
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexFlow: "column",
                        gap: "4px",
                      }}
                    >
                      {
                        data.auto_budgets.map((v) => (
                          <div
                            style={{
                              borderBottom: "dashed 1px #CCCCCC",
                              padding: "2px 8px",
                              display: "flex",
                              gap: "8px",
                            }}
                          >
                            <div>
                              {format(v.start_date, "YYYY年M月")}
                            </div>
                            <div>
                              ~
                            </div>
                            <div>
                              {v.end_date ? format(v.end_date, "YYYY年M月") : "指定なし"}
                            </div>
                            <div>
                              {`¥${v.budget.toLocaleString()}`}
                            </div>
                          </div>
                        ))
                      }
                    </div>
                  </div>
                )
              }
              {
                !!data.auto_other_costs.length && (
                  <div className="block_area">
                    <div className="title_area">
                      その他のコスト
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexFlow: "column",
                        gap: "4px",
                      }}
                    >
                      {
                        data.auto_other_costs.map((v) => (
                          <div
                            style={{
                              borderBottom: "dashed 1px #CCCCCC",
                              padding: "2px 8px",
                              display: "flex",
                              gap: "8px",
                            }}
                          >
                            <div>
                              {format(v.start_date, "YYYY年M月")}
                            </div>
                            <div>
                              ~
                            </div>
                            <div>
                              {v.end_date ? format(v.end_date, "YYYY年M月") : "指定なし"}
                            </div>
                            <div>
                              {v.name}
                            </div>
                            <div>
                              {`¥${v.cost.toLocaleString()}`}
                            </div>
                          </div>
                        ))
                      }
                    </div>
                  </div>
                )
              }
            </div>
          </div>
        ))
      }
    </>
  )

  useEffect(() => {
    setCsvErrorList(dataList?.errors || [])
  }, [dataList?.errors])

  useEffect(() => {
    setStep(1)
    setFileData(undefined)
    setCsvErrorList([])
    setOpenList(openListInit)
    setTotalCount(0)
    setDisplayNum({
      create: 1,
      update: 1,
      destroy: 1,
      no_change: 1,
    })
  }, [isDisplay])

  return (
    <Modal
      show={isDisplay}
      wrapperClass="import_project"
      closeCallback={closeCallback}
      closeable={!isLoading}
    >
      <>
        <div className="modal_title">
          プロジェクトを一括登録する
        </div>
        {
          step === 1 && (
            <>
              <div className="modal_detail">
                <div className="step_title">
                  STEP.1 プロジェクト一括登録用CSVファイルのダウンロード
                </div>
                <div className="step_detail">
                  <div className="detail_text_area">
                    <div>
                      プロジェクト一括登録用CSVファイルのダウンロードをおこない、編集してください。
                    </div>
                    <div className="red">
                      ※ExcelでCSVを開く方はShift-JISを選択して下さい。
                    </div>
                  </div>
                  <div className="detail_button_area">
                    {
                      isDownloading ? (
                        <>
                          <FontAwesomeIcon icon={['fas', 'spinner']} spin />
                        </>
                      ) : (
                        <>
                          <button
                            type="button"
                            className="download"
                            onClick={() => downloadCsv('Shift-JIS')}
                            disabled={isDownloading}
                          >
                            プロジェクト一括登録用(Shift-JIS)
                          </button>
                          <button
                            type="button"
                            className="download"
                            onClick={() => downloadCsv('UTF-8')}
                            disabled={isDownloading}
                          >
                            プロジェクト一括登録用(UTF-8)
                          </button>
                        </>
                      )
                    }
                  </div>
                </div>
                <div className="step_title">
                  STEP.2 プロジェクト一括登録用CSVファイルの編集
                </div>
                <div className="step_detail">
                  <div className="detail_text_area">
                    <div>
                      <p>
                        ダウンロードしたプロジェクト一括登録用CSVファイルを編集してください。
                      </p>
                    </div>
                    <div
                      style={{
                        maxHeight: "calc(83vh - 370px)",
                        overflowY: "scroll",
                      }}
                    >
                      <table>
                        <tr>
                          <th>
                            項目名
                          </th>
                          <th>
                            必須 / 任意
                          </th>
                          <th>
                            概要
                          </th>
                        </tr>
                        <tr>
                          <td>
                            プロジェクト名(変更禁止)
                          </td>
                          <td>
                            -
                          </td>
                          <td>
                            <p className="required">
                              プロジェクトを判定するための項目です。変更しないでください。
                            </p>
                          </td>
                        </tr>
                        <tr>
                          <td>
                            プロジェクト名(変更後)
                          </td>
                          <td>
                            任意
                          </td>
                          <td>
                            <p>
                              プロジェクト名を変更したい場合、新しいプロジェクト名を入力してください。
                            </p>
                            <p className="example">
                              例) 新テストプロジェクト
                            </p>
                          </td>
                        </tr>
                        <tr>
                          <td>
                            プロジェクトコード
                          </td>
                          <td>
                            任意
                          </td>
                          <td>
                            <p>
                              プロジェクトに任意の情報を設定したい場合、入力してください。
                            </p>
                            <p className="example">
                              例) PJ-1
                            </p>
                          </td>
                        </tr>
                        <tr>
                          <td>
                            プロジェクトカラー
                          </td>
                          <td>
                            任意
                          </td>
                          <td>
                            <p>
                              プロジェクトにカラーを設定したい場合、入力してください。
                            </p>
                            <p className="example">
                              例) レッド
                            </p>
                            <div
                              style={{
                                border: "solid 1px #CCCCCC",
                                padding: "8px",
                                margin: "8px 0",
                              }}
                            >
                              <div
                                style={{
                                  marginBottom: "8px",
                                }}
                              >
                                設定できるカラー一覧
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  flexFlow: "column",
                                  gap: "8px",
                                }}
                              >
                                {
                                  COLOR_LIST.map((color) => (
                                    <div
                                      style={{
                                        display: "flex",
                                        gap: "4px",
                                      }}
                                    >
                                      {
                                        color.values.map((v) => (
                                          <div
                                            style={{
                                              display: "flex",
                                              alignItems: "center",
                                              gap: "4px",
                                              fontSize: "10px",
                                            }}
                                          >
                                            <div
                                              style={{
                                                backgroundColor: v.color,
                                                width: "10px",
                                                height: "10px",
                                                borderRadius: "50%",
                                              }}
                                            />
                                            {v.text}
                                          </div>
                                        ))
                                      }
                                    </div>
                                  ))
                                }
                              </div>
                            </div>
                            <p>
                              入力なしの場合はランダムに設定されます。
                            </p>
                          </td>
                        </tr>
                        <tr>
                          <td>
                            開始予定日
                          </td>
                          <td>
                            任意
                          </td>
                          <td>
                            <p>
                              プロジェクトの開始予定日を入力してください。
                            </p>
                            <p>
                              形式は「YYYY/MM/DD」の形式で入力してください。
                            </p>
                            <p className="example">
                              例) 2024/09/01
                            </p>
                            <p>
                              {`入力なしの場合は今日(${nowDate()})が設定されます。`}
                            </p>
                          </td>
                        </tr>
                        <tr>
                          <td>
                            終了予定日
                          </td>
                          <td>
                            任意
                          </td>
                          <td>
                            <p>
                              プロジェクトの終了予定日を設定したい場合、入力してください。
                            </p>
                            <p>
                              形式は「YYYY/MM/DD」の形式で入力してください。
                            </p>
                            <p className="example">
                              例) 2024/09/30
                            </p>
                          </td>
                        </tr>
                        <tr>
                          <td>
                            業務
                          </td>
                          <td>
                            任意
                          </td>
                          <td>
                            <p>
                              プロジェクトに設定したい業務名を入力して下さい。
                            </p>
                            <p>
                              複数設定したいときは業務名の間に「:」を入れてください。
                            </p>
                            <p>
                              また、業務コードを設定したいときは業務名と業務コードの間に「$」を入れてください。
                            </p>
                            <p className="example">
                              例) 事務$GM0001:営業:移動$GM0003
                            </p>
                          </td>
                        </tr>
                        <tr>
                          <td>
                            プロジェクト表示チーム
                          </td>
                          <td>
                            <p className="required">
                              任意 ※1
                            </p>
                          </td>
                          <td>
                            <p>
                              プロジェクトを表示できるチーム名を入力して下さい。
                            </p>
                            <p>
                              複数設定したいときはチーム名の間に「:」を入れてください。
                            </p>
                            <p className="example">
                              例) チームA:チームB:チームC
                            </p>
                            <p className="required">
                              ※1 プロジェクト表示チームまたはプロジェクト表示メンバーのどちらかは必ず入力して下さい。
                            </p>
                          </td>
                        </tr>
                        <tr>
                          <td>
                            プロジェクト表示メンバー
                          </td>
                          <td>
                            <p className="required">
                              任意 ※1
                            </p>
                          </td>
                          <td>
                            <p>
                              プロジェクトを表示できるメンバーのメールアドレスを入力して下さい。
                            </p>
                            <p>
                              複数設定したいときはメールアドレスの間に「:」を入れてください。
                            </p>
                            <p className="example">
                              例) test_a@example.com:test_b@example.com
                            </p>
                            <p className="required">
                              ※1 プロジェクト表示チームまたはプロジェクト表示メンバーのどちらかは必ず入力して下さい。
                            </p>
                          </td>
                        </tr>
                        <tr>
                          <td>
                            プロジェクト参加チーム
                          </td>
                          <td>
                            <p className="required">
                              任意 ※2
                            </p>
                          </td>
                          <td>
                            <p>
                              プロジェクトを利用するチーム名を入力して下さい。
                            </p>
                            <p>
                              複数設定したいときはチーム名の間に「:」を入れてください。
                            </p>
                            <p className="example">
                              例) チームA:チームB:チームC
                            </p>
                            <p className="required">
                              ※2 プロジェクト参加チームまたはプロジェクト参加メンバーのどちらかは必ず入力して下さい。
                            </p>
                          </td>
                        </tr>
                        <tr>
                          <td>
                            プロジェクト参加メンバー
                          </td>
                          <td>
                            <p className="required">
                              任意 ※2
                            </p>
                          </td>
                          <td>
                            <p>
                              プロジェクトを利用するメンバーのメールアドレスを入力して下さい。
                            </p>
                            <p>
                              複数設定したいときはメールアドレスの間に「:」を入れてください。
                            </p>
                            <p className="example">
                              例) test_a@example.com:test_b@example.com
                            </p>
                            <p className="required">
                              ※2 プロジェクト参加チームまたはプロジェクト参加メンバーのどちらかは必ず入力して下さい。
                            </p>
                          </td>
                        </tr>
                        <tr>
                          <td>
                            予定時間
                          </td>
                          <td>
                            任意
                          </td>
                          <td>
                            <p>
                              プロジェクトに設定したい予定時間の開始月・終了月・時間を入力して下さい。
                            </p>
                            <p>
                              設定したいときは開始月・終了月・時間の間に「$」を入れてください。
                            </p>
                            <p>
                              また、複数設定したいときは間に「:」を入れてください。
                            </p>
                            <p className="example">
                              例) 2025/12/01$2026/06/01$100:2026/07/01$$60
                            </p>
                          </td>
                        </tr>
                        {
                          isCost && (
                            <>
                              <tr>
                                <td>
                                  予算
                                </td>
                                <td>
                                  任意
                                </td>
                                <td>
                                  <p>
                                    プロジェクトに設定したい予算の開始月・終了月・金額を入力して下さい。
                                  </p>
                                  <p>
                                    設定したいときは開始月・終了月・金額の間に「$」を入れてください。
                                  </p>
                                  <p>
                                    また、複数設定したいときは間に「:」を入れてください。
                                  </p>
                                  <p className="example">
                                    例) 2025/12/01$2026/06/01$20000:2026/07/01$$100000
                                  </p>
                                </td>
                              </tr>
                              <tr>
                                <td>
                                  その他のコスト
                                </td>
                                <td>
                                  任意
                                </td>
                                <td>
                                  <p>
                                    プロジェクトに設定したいその他のコストの開始月・終了月・項目・金額を入力して下さい。
                                  </p>
                                  <p>
                                    設定したいときは開始月・終了月・項目・金額の間に「$」を入れてください。
                                  </p>
                                  <p>
                                    また、複数設定したいときは間に「:」を入れてください。
                                  </p>
                                  <p className="example">
                                    例) 2025/12/01$2026/06/01$交際費$10000:2025/12/01$2026/06/01$移動費$50000:2026/01/01$$交際費$20000
                                  </p>
                                </td>
                              </tr>
                            </>
                          )
                        }
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )
        }
        {
          step === 2 && (
            <>
              <div className="modal_detail">
                <div className="step_title">
                  STEP.3 プロジェクト一括登録用CSVファイルのアップロード
                </div>
                <div className="step_detail">
                  <div className="detail_text_area">
                    <div>
                      編集したプロジェクト一括登録用CSVファイルをアップロードしてください。
                    </div>
                  </div>
                  <div className="upload_area">
                    <label htmlFor="import_csv" style={{ cursor: isLoading ? "not-allowed" : "pointer" }}>
                      プロジェクト一括登録用CSVファイルをアップロードしてください。
                      {
                        isLoading ? (
                          <LoadingSquare>
                            <>
                              ファイルをアップロード・検証しています…
                            </>
                          </LoadingSquare>
                        ) : (
                          <input
                            type="file"
                            id="import_csv"
                            name="import_csv"
                            accept="text/csv"
                            onChange={(e) => uploadCsv(e.currentTarget.files)}
                            onClick={(e) => { e.currentTarget.value = "" }}
                          />
                        )
                      }
                    </label>
                  </div>
                  {
                    !!csvErrorList.length && (
                      <div className="error_message">
                        {
                          csvErrorList.map((error) => (
                            <p>
                              {`${error.row_number}行目:${error.column_name !== "共通" ? `[${error.column_name}]` : ""}${error.message}`}
                            </p>
                          ))
                        }
                      </div>
                    )
                  }
                </div>
              </div>
            </>
          )
        }
        {
          step === 3 && (
            <>
              <div className="modal_detail">
                <div className="step_title">
                  STEP.4 編集するプロジェクトの確認
                </div>
                <div className="step_detail">
                  <div className="detail_text_area">
                    <div>
                      以下の内容で登録をおこないます。
                    </div>
                  </div>
                  <div className="detail_result_area">
                    {
                      resultDisplay ? (
                        <>
                          <div className="result_row_header">
                            <div className="name_area">
                              プロジェクト名(プロジェクトコード)
                            </div>
                            <div className="schedule_area">
                              予定
                            </div>
                            <div className="detail_area">
                              詳細
                            </div>
                          </div>
                        </>
                      ) : (
                        <div
                          style={{
                            padding: "8px 0",
                            fontSize: "14px",
                          }}
                        >
                          総件数が1000件を超えたため、件数のみの表示になります
                        </div>
                      )
                    }
                    {
                      !!dataList?.count.create && (
                        <>
                          {buildProjectActionBar("create")}
                          {openList.includes("create") && buildProjectList("create")}
                          {
                            resultDisplay
                            && openList.includes("create")
                            && Math.ceil(dataList.count.create / DISPLAY_LIMIT) > displayNum.create && (
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  padding: "8px",
                                }}
                              >
                                <button
                                  type="button"
                                  style={{
                                    border: "solid 1px #666666",
                                    borderRadius: "20px",
                                    fontSize: "10px",
                                    padding: "4px 16px",
                                  }}
                                  onClick={() => setDisplayNum((current) => ({ ...current, create: current.create + 1 }))}
                                >
                                  さらに表示する
                                </button>
                              </div>
                            )
                          }
                        </>
                      )
                    }
                    {
                      !!dataList?.count.destroy && (
                        <>
                          {buildProjectActionBar("destroy")}
                          {openList.includes("destroy") && buildProjectList("destroy")}
                          {
                            resultDisplay
                            && openList.includes("destroy")
                            && Math.ceil(dataList.count.destroy / DISPLAY_LIMIT) > displayNum.destroy && (
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  padding: "8px",
                                }}
                              >
                                <button
                                  type="button"
                                  style={{
                                    border: "solid 1px #666666",
                                    borderRadius: "20px",
                                    fontSize: "10px",
                                    padding: "4px 16px",
                                  }}
                                  onClick={() => setDisplayNum((current) => ({ ...current, destroy: current.destroy + 1 }))}
                                >
                                  さらに表示する
                                </button>
                              </div>
                            )
                          }
                        </>
                      )
                    }
                    {
                      !!dataList?.count.update && (
                        <>
                          {buildProjectActionBar("update")}
                          {openList.includes("update") && buildProjectList("update")}
                          {
                            resultDisplay
                            && openList.includes("update")
                            && Math.ceil(dataList.count.update / DISPLAY_LIMIT) > displayNum.update && (
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  padding: "8px",
                                }}
                              >
                                <button
                                  type="button"
                                  style={{
                                    border: "solid 1px #666666",
                                    borderRadius: "20px",
                                    fontSize: "10px",
                                    padding: "4px 16px",
                                  }}
                                  onClick={() => setDisplayNum((current) => ({ ...current, update: current.update + 1 }))}
                                >
                                  さらに表示する
                                </button>
                              </div>
                            )
                          }
                        </>
                      )
                    }
                    {
                      !!dataList?.count.no_change && (
                        <>
                          {buildProjectActionBar("no_change")}
                          {openList.includes("no_change") && buildProjectList("no_change")}
                          {
                            resultDisplay
                            && openList.includes("no_change")
                            && Math.ceil(dataList.count.no_change / DISPLAY_LIMIT) > displayNum.no_change && (
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  padding: "8px",
                                }}
                              >
                                <button
                                  type="button"
                                  style={{
                                    border: "solid 1px #666666",
                                    borderRadius: "20px",
                                    fontSize: "10px",
                                    padding: "4px 16px",
                                  }}
                                  onClick={() => setDisplayNum((current) => ({ ...current, no_change: current.no_change + 1 }))}
                                >
                                  さらに表示する
                                </button>
                              </div>
                            )
                          }
                        </>
                      )
                    }
                  </div>
                </div>
              </div>
            </>
          )
        }
        {
          step === 4 && (
            <>
              <div className="modal_detail">
                <div className="step_title">
                  プロジェクト一括登録を受け付けました
                </div>
                <div className="step_detail">
                  <div className="detail_text_area">
                    <div
                      style={{
                        textAlign: "center",
                        fontSize: "14px",
                      }}
                    >
                      <p>
                        プロジェクト一括登録を受け付けました。
                      </p>
                      <p>
                        受け付けしたCSVファイルは順次処理されます。
                      </p>
                      <p>
                        {`おおよそ${Math.ceil(totalCount / 160) + 1}分程度の時間がかかる予定です。`}
                      </p>
                      <p>
                        登録が完了次第、メールにてお知らせいたします。
                      </p>
                      <p>
                        登録完了までしばらくお待ちください。
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )
        }
        {buildButtonArea()}
      </>
    </Modal>
  )
}

export default ImportProjectModal
